import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Box, 
  Grid, 
  Typography, 
  Paper,
  Skeleton,
  Button,
  Alert,
  AlertTitle,
  FormControlLabel,
  Switch,
  Tooltip
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import MetricCard from './MetricCard';
import { 
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import { 
  fetchDailyEmailSummaryV2,
  fetchSegmentSummaryV2,
  fetchGroupPerformance,
  fetchGroupSummary,
  fetchGroupSegmentSummary,
  DateRange, 
  EmailAnalyticsSummaryData
} from '../../services/analyticsService';
import { format, eachDayOfInterval } from 'date-fns';
import { useUser } from '../../contexts/UserContext';

import { getSegmentColor } from '../../utils/segmentColors';
import ContactListModal from '../ContactListModal';
import GeographicMapModal from './GeographicMapModal';
import apiService from '../../services/apiService';

interface Props {
  dateRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
  groupId?: number;
  clientId?: number | null;
  includeSpamChecker?: boolean;
  onSpamCheckerChange?: (includeSpam: boolean) => void;
}

type ViewMode = 'count' | 'rate';

const AnalyticsOverview: React.FC<Props> = ({ dateRange, onDateRangeChange, groupId, clientId, includeSpamChecker = false, onSpamCheckerChange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isFetchingRef = useRef(false);
  const [data, setData] = useState<EmailAnalyticsSummaryData | null>(null);
  const [groupSummaryData, setGroupSummaryData] = useState<any>(null); // Store group summary data for client_names
  const [sendsByDay, setSendsByDay] = useState<Array<{date: string, sends: number}>>([]);
  const [opensByDayDual, setOpensByDayDual] = useState<Array<{date: string, total_opens: number, unique_opens: number}>>([]);
  const [clicksByDayDual, setClicksByDayDual] = useState<Array<{date: string, total_clicks: number, unique_clicks: number}>>([]);
  const [opensBySegment, setOpensBySegment] = useState<Array<{
    segment: string;
    opens: number;
    sends: number;
    open_rate: number;
    clicks: number;
    click_rate: number;
  }>>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('count');
  const [showSends, setShowSends] = useState(true);
  const [showOpens, setShowOpens] = useState(true);
  const [showClicks, setShowClicks] = useState(true);
  
  // Contact modal state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedMetricForModal, setSelectedMetricForModal] = useState<'sent' | 'opens' | 'clicks' | null>(null);
  
  // Map modal state
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedMetricForMap, setSelectedMetricForMap] = useState<'opens' | 'clicks'>('opens');
  const [mapData, setMapData] = useState<any[]>([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const showUnique = false; // Always show total counts (removed toggle)
  const [clickRateMode, setClickRateMode] = useState<'send' | 'ctor'>('send');
  const { selectedClientId } = useUser();
  
  // Use provided clientId if available, otherwise fall back to selectedClientId
  const effectiveClientId = clientId || selectedClientId;
  
  // Debug logging
  console.log('AnalyticsOverview client IDs:', {
    clientId,
    selectedClientId,
    effectiveClientId
  });

  // Check if this is group analytics mode
  const isGroupMode = !!groupId;
  
  // Contact modal handlers - disabled for group analytics
  const handleOpenContactModal = (metric: 'sent' | 'opens' | 'clicks') => {
    if (isGroupMode) {
      // Group analytics doesn't support contact drill-down
      return;
    }
    setSelectedMetricForModal(metric);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  // Map modal handlers
  const handleOpenMapModal = async (metric: 'opens' | 'clicks') => {
    setSelectedMetricForMap(metric);
    setMapModalOpen(true);
    await fetchGeographicData(metric);
  };

  const fetchGeographicData = async (metric: 'opens' | 'clicks') => {
    // Group analytics doesn't support geographic data
    if (isGroupMode || !effectiveClientId) return;

    try {
      setMapLoading(true);
      setMapError(null);

      // Use the same date format and parameters as the contacts modal for consistency
      const response = await apiService.get(`/api/analytics/engagement/${metric}`, {
        params: {
          client_id: effectiveClientId,
          date_range: dateRange.date_range,
          start_date: dateRange.startDate?.toISOString(),
          end_date: dateRange.endDate?.toISOString(),
          include_spam: !includeSpamChecker,
          include_geographic: true,
          limit: 5000 // Get more data for geographic mapping
        }
      });
      
      const data = response.data as any;
      const contacts = data.contacts || [];
      
      // Group contacts by latitude/longitude coordinates
      const geographicData = new Map<string, any>();
      
      contacts.forEach((contact: any) => {
        if (contact.latitude && contact.longitude) {
          const key = `${contact.latitude},${contact.longitude}`;
          
          if (!geographicData.has(key)) {
            geographicData.set(key, {
              latitude: parseFloat(contact.latitude),
              longitude: parseFloat(contact.longitude),
              count: 0,
              contacts: []
            });
          }
          
          const locationData = geographicData.get(key);
          if (metric === 'opens') {
            locationData.count += contact.open_count || 1;
          } else if (metric === 'clicks') {
            locationData.count += contact.click_count || 1;
          } else {
            locationData.count += 1;
          }
          
          locationData.contacts.push({
            id: contact.id,
            first_name: contact.first_name,
            last_name: contact.last_name,
            email_address: contact.email_address
          });
        }
      });
      
      setMapData(Array.from(geographicData.values()));
    } catch (err: any) {
      console.error('Geographic data fetch error:', err);
      setMapError(`Failed to load geographic data: ${err.response?.status || 'Unknown error'}`);
      setMapData([]);
    } finally {
      setMapLoading(false);
    }
  };

  const handleCloseMapModal = () => {
    setMapModalOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!effectiveClientId && !groupId) return;
      
      // Prevent duplicate calls in React StrictMode
      if (isFetchingRef.current) return;
      
      try {
        isFetchingRef.current = true;
        setLoading(true);
        setError(null);
        
        // Group analytics mode
        if (isGroupMode && groupId) {
          console.log('Fetching group analytics data:', { groupId, startDate: dateRange.startDate?.toISOString().split('T')[0], endDate: dateRange.endDate?.toISOString().split('T')[0] });
          
          // Fetch group summary data
          const startDate = dateRange.startDate?.toISOString().split('T')[0];
          const endDate = dateRange.endDate?.toISOString().split('T')[0];
          
          let summaryData, performanceData;
          try {
            [summaryData, performanceData] = await Promise.all([
              fetchGroupSummary(groupId, startDate, endDate, includeSpamChecker),
              fetchGroupPerformance(groupId, startDate, endDate, includeSpamChecker)
            ]);
            console.log('Group analytics data received:', { summaryData, performanceData });
          } catch (fetchError: any) {
            console.error('Error fetching group summary/performance:', fetchError);
            setError(fetchError.response?.data?.detail || fetchError.message || 'Failed to load group analytics data');
            setLoading(false);
            isFetchingRef.current = false;
            return;
          }
          
          // Store group summary data for accessing client_names
          setGroupSummaryData(summaryData);
          
          // Set data from group summary
          setData({
            total_sends: summaryData.totals.total_sent,
            total_opens: summaryData.totals.total_opened,
            total_clicks: summaryData.totals.total_clicks,
            unique_opens: summaryData.totals.unique_opens,
            unique_clicks: summaryData.totals.unique_clicks,
            clean_opens: summaryData.totals.total_opened,
            clean_clicks: summaryData.totals.total_clicks,
            open_rate: summaryData.averages.avg_open_rate,
            click_rate: summaryData.averages.avg_click_rate,
            click_to_open_rate: summaryData.averages.avg_click_to_open_rate,
            unique_open_rate: summaryData.totals.total_sent > 0 ? (summaryData.totals.unique_opens / summaryData.totals.total_sent * 100) : 0,
            unique_click_rate: summaryData.totals.total_sent > 0 ? (summaryData.totals.unique_clicks / summaryData.totals.total_sent * 100) : 0,
            unique_click_to_open_rate: summaryData.totals.unique_opens > 0 && summaryData.totals.unique_clicks > 0 ? (summaryData.totals.unique_clicks / summaryData.totals.unique_opens * 100) : 0,
            clean_open_rate: summaryData.averages.avg_open_rate,
            clean_click_rate: summaryData.averages.avg_click_rate,
            clean_click_to_open_rate: summaryData.averages.avg_click_to_open_rate,
            spam_open_count: 0,
            spam_click_count: 0,
            spam_open_rate: 0,
            spam_click_rate: 0
          });
          
          // Set time series data from performance data
          const opensByDayDualData = performanceData.metrics.map(day => ({
            date: day.date,
            total_opens: day.total_opened,
            unique_opens: day.unique_opens
          }));
          setOpensByDayDual(opensByDayDualData);
          
          const clicksByDayDualData = performanceData.metrics.map(day => ({
            date: day.date,
            total_clicks: day.total_clicks,
            unique_clicks: day.unique_clicks
          }));
          setClicksByDayDual(clicksByDayDualData);
          
          const sendsByDayData = performanceData.metrics.map(day => ({
            date: day.date,
            sends: day.total_sent
          }));
          setSendsByDay(sendsByDayData);
          
          // Fetch group segment performance data (optional - don't block if it fails)
          try {
            const groupSegmentData = await fetchGroupSegmentSummary(
              groupId,
              dateRange.startDate?.toISOString().split('T')[0],
              dateRange.endDate?.toISOString().split('T')[0],
              includeSpamChecker
            );
            
            // Transform to match opensBySegment format
            const segmentDataFormatted = groupSegmentData.segments.map(seg => ({
              segment: seg.segment_name,
              opens: seg.total_opened,
              sends: seg.total_sent,
              open_rate: seg.open_rate,
              clicks: includeSpamChecker ? seg.total_clicks_unfiltered : seg.total_clicks_ip_filtered,
              click_rate: includeSpamChecker ? seg.click_rate_unfiltered : seg.click_rate_ip_filtered
            }));
            setOpensBySegment(segmentDataFormatted);
          } catch (err: any) {
            console.error('Error fetching group segment summary (non-critical):', err);
            // Don't set error here - this is optional data
            setOpensBySegment([]);
          }
          
          // Make sure loading is set to false and ref is cleared
          setLoading(false);
          isFetchingRef.current = false;
          return;
        }
        
        // Client analytics mode (existing logic)
        const data = await fetchDailyEmailSummaryV2(
          effectiveClientId!,
          dateRange.startDate?.toISOString().split('T')[0],
          dateRange.endDate?.toISOString().split('T')[0],
          !includeSpamChecker // Invert logic: when "Filter out spam" is false, include spam (true)
        );
        
        console.log('Using V2 daily_email_summary endpoint:', {
          client_id: effectiveClientId,
          start_date: dateRange.startDate?.toISOString().split('T')[0],
          end_date: dateRange.endDate?.toISOString().split('T')[0],
          include_spam: !includeSpamChecker,
          summary: data.summary,
          time_series_count: data.time_series.length
        });
        
        // Set data directly from V2 endpoint
        setData({
          total_sends: data.summary.total_sends,
          total_opens: data.summary.total_opens,
          total_clicks: data.summary.total_clicks,
          unique_opens: data.summary.unique_opens,
          unique_clicks: data.summary.unique_clicks,
          clean_opens: data.summary.total_opens,
          clean_clicks: data.summary.total_clicks,
          open_rate: data.summary.open_rate,
          click_rate: data.summary.click_rate,
          click_to_open_rate: data.summary.click_to_open_rate,
          unique_open_rate: data.summary.unique_opens > 0 ? (data.summary.unique_opens / data.summary.total_sends * 100) : 0,
          unique_click_rate: data.summary.unique_clicks > 0 ? (data.summary.unique_clicks / data.summary.total_sends * 100) : 0,
          unique_click_to_open_rate: data.summary.unique_clicks > 0 && data.summary.unique_opens > 0 ? (data.summary.unique_clicks / data.summary.unique_opens * 100) : 0,
          clean_open_rate: data.summary.open_rate,
          clean_click_rate: data.summary.click_rate,
          clean_click_to_open_rate: data.summary.click_to_open_rate,
          spam_open_count: 0,
          spam_click_count: 0,
          spam_open_rate: 0,
          spam_click_rate: 0
        });
        
        // Set time series data from V2 endpoint
        const opensByDayDualData = data.time_series.map(day => ({
          date: day.date,
          total_opens: day.total_opens,
          unique_opens: day.unique_opens
        }));
        setOpensByDayDual(opensByDayDualData);
        
        const clicksByDayDualData = data.time_series.map(day => ({
          date: day.date,
          total_clicks: day.total_clicks,
          unique_clicks: day.unique_clicks
        }));
        setClicksByDayDual(clicksByDayDualData);
        
        const sendsByDayData = data.time_series.map(day => ({
          date: day.date,
          sends: day.total_sends
        }));
        setSendsByDay(sendsByDayData);
        
        // Fetch segment performance data from V2 endpoint (REQUIRED - will fail if not implemented)
        const segmentData = await fetchSegmentSummaryV2(
          effectiveClientId!,
          dateRange.startDate?.toISOString().split('T')[0],
          dateRange.endDate?.toISOString().split('T')[0],
          !includeSpamChecker
        );
        
        // Transform to match existing format
        const opensBySegmentData = segmentData.segments.map(segment => ({
          segment: segment.segment_name,
          opens: segment.total_opened,
          open_rate: segment.open_rate,
          clicks: includeSpamChecker ? segment.total_clicks_unfiltered : segment.total_clicks_ip_filtered,
          click_rate: includeSpamChecker ? segment.click_rate_unfiltered : segment.click_rate_ip_filtered,
          click_to_open_rate: includeSpamChecker ? segment.click_to_open_rate_unfiltered : segment.click_to_open_rate_ip_filtered,
          sends: segment.total_sent
        }));
        
        console.log('Using V2 segment summary endpoint:', {
          total_segments: segmentData.total_count,
          segments: segmentData.segments.length
        });
        setOpensBySegment(opensBySegmentData);
        
      } catch (err: any) {
        console.error('Error fetching analytics data (V2):', err);
        setError(err.message || 'Failed to load analytics data');
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchData();
  }, [dateRange, effectiveClientId, groupId, includeSpamChecker, isGroupMode]);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const createDailyData = (startDate: Date, endDate: Date, opensMap: Map<string, number>) => {
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    return days.map(date => {
      const dateKey = format(date, 'yyyy-MM-dd');
      return {
        x: format(date, 'MMM d'),
        y: opensMap.get(dateKey) || 0,
        num_days: 1,
        originalDate: dateKey
      };
    });
  };

  const createWeeklyRollups = (startDate: Date, endDate: Date, opensMap: Map<string, number>) => {
    const rollups = [];
    let current = new Date(startDate);
    while (current <= endDate) {
      const weekStart = current;
      let weekEnd = new Date(current);
      weekEnd.setDate(current.getDate() + 6);
      if (weekEnd > endDate) weekEnd = endDate;
      
      const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
      const num_days = days.length;
      let sum_opens = 0;
      days.forEach(day => {
        const dateKey = format(day, 'yyyy-MM-dd');
        sum_opens += opensMap.get(dateKey) || 0;
      });
      
      rollups.push({
        x: format(weekEnd, 'MMM d'),
        y: sum_opens,
        num_days,
        originalDate: format(weekEnd, 'yyyy-MM-dd')
      });
      current = new Date(weekEnd);
      current.setDate(current.getDate() + 1);
    }
    return rollups;
  };

  const createMonthlyRollups = (startDate: Date, endDate: Date, opensMap: Map<string, number>) => {
    const rollups = [];
    let current = new Date(startDate);
    current.setDate(1); // Start from beginning of month
    
    while (current <= endDate) {
      const monthStart = current > startDate ? current : startDate;
      let monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
      if (monthEnd > endDate) monthEnd = endDate;
      
      const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
      const num_days = days.length;
      let sum_opens = 0;
      days.forEach(day => {
        const dateKey = format(day, 'yyyy-MM-dd');
        sum_opens += opensMap.get(dateKey) || 0;
      });
      
      rollups.push({
        x: format(current, 'MMM yyyy'),
        y: sum_opens,
        num_days,
        originalDate: format(current, 'yyyy-MM')
      });
      current.setMonth(current.getMonth() + 1);
    }
    return rollups;
  };

  const chartData = useMemo(() => {
    if (!opensByDayDual || opensByDayDual.length === 0 || !dateRange.startDate || !dateRange.endDate) {
      return { opens: [], clicks: [], sends: [] };
    }

    // Use dual data and apply showUnique filter
    const opensMap = new Map(opensByDayDual.map(day => [day.date, showUnique ? day.unique_opens : day.total_opens]));
    const clicksMap = new Map(clicksByDayDual.map(day => [day.date, showUnique ? day.unique_clicks : day.total_clicks]));
    const sendsMap = new Map(sendsByDay.map(day => [day.date, day.sends]));

    const startDate = dateRange.startDate;
    const endDate = dateRange.endDate;

    // Calculate total days in the range
    const totalDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    let processedOpensData;
    let processedClicksData;
    let processedSendsData;
    
    if (totalDays >= 365) {
      // For 12+ months, show monthly rollups
      processedOpensData = createMonthlyRollups(startDate, endDate, opensMap);
      processedClicksData = createMonthlyRollups(startDate, endDate, clicksMap);
      processedSendsData = createMonthlyRollups(startDate, endDate, sendsMap);
    } else if (totalDays > 90) {
      // For 90+ days, show weekly rollups
      processedOpensData = createWeeklyRollups(startDate, endDate, opensMap);
      processedClicksData = createWeeklyRollups(startDate, endDate, clicksMap);
      processedSendsData = createWeeklyRollups(startDate, endDate, sendsMap);
    } else {
      // For shorter periods (including Last 7 Days), show daily data
      processedOpensData = createDailyData(startDate, endDate, opensMap);
      processedClicksData = createDailyData(startDate, endDate, clicksMap);
      processedSendsData = createDailyData(startDate, endDate, sendsMap);
    }

    // Always return count data for the main Email Engagement Over Time graph
    return {
      opens: processedOpensData.map(item => ({
        x: item.x,
        y: item.y,
        originalDate: item.originalDate
      })),
      clicks: processedClicksData.map(item => ({
        x: item.x,
        y: item.y,
        originalDate: item.originalDate
      })),
      sends: processedSendsData.map(item => ({
        x: item.x,
        y: item.y,
        originalDate: item.originalDate
      }))
    };
  }, [opensByDayDual, clicksByDayDual, sendsByDay, dateRange, showUnique]);


  const chartTitle = 'Email Engagement Over Time';
  const yAxisLabel = 'Count';

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
          <Button 
            variant="outlined" 
            onClick={() => onDateRangeChange({ date_range: 'last_30_days' })}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Alert>
      )}

      {/* Filter Toggles and Group Info */}
      <Box display="flex" justifyContent="space-between" alignItems="center" className="mb-6">
        {/* Group Members Label - only show in group mode */}
        {isGroupMode && groupSummaryData?.clients?.client_names && groupSummaryData.clients.client_names.length > 0 && (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Group Members:
            </Typography>
            <Tooltip
              title={
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Participating Agencies:
                  </Typography>
                  {groupSummaryData.clients.client_names.map((name: string, idx: number) => (
                    <Typography key={idx} variant="body2">
                      • {name}
                    </Typography>
                  ))}
                </Box>
              }
              arrow
              placement="bottom-start"
            >
              <HelpOutlineIcon 
                sx={{ 
                  fontSize: '1rem', 
                  color: 'text.secondary',
                  cursor: 'help'
                }} 
              />
            </Tooltip>
          </Box>
        )}
        
        <Box display="flex" alignItems="center" gap={3}>
          {/* Spam Filter Toggle */}
          <Box display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeSpamChecker}
                  onChange={(e) => {
                    onSpamCheckerChange?.(e.target.checked);
                  }}
                  size="small"
                />
              }
              label="Filter out likely spam checker clicks"
              labelPlacement="start"
              sx={{ 
                margin: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }
              }}
            />
            <Tooltip
              title={
                <Box sx={{ maxWidth: 350 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'white' }}>
                    Filter out likely spam checker clicks
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, color: 'white' }}>
                    When enabled, excludes opens and clicks from IP addresses identified as spam checkers or automated systems.
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, ml: 1, color: 'white' }}>
                    • IP-based behavioral analysis
                    <br />
                    • Volume and pattern detection
                    <br />
                    • Multi-factor spam identification
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'rgba(255, 255, 255, 0.8)' }}>
                    Note: This filtering system uses advanced statistical analysis and proprietary algorithms. 
                    While extensively tested and refined, it may occasionally classify some legitimate 
                    user actions as spam checker activity, or miss some automated activity. 
                    Results should be considered as refined estimates rather than absolute values.
                  </Typography>
                </Box>
              }
              arrow
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 400,
                    fontSize: '0.875rem',
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    color: 'white',
                    '& .MuiTooltip-arrow': {
                      color: 'rgba(0, 0, 0, 0.9)',
                    },
                  },
                },
              }}
            >
              <HelpOutlineIcon 
                sx={{ 
                  fontSize: '1rem', 
                  color: 'text.secondary',
                  cursor: 'help'
                }} 
              />
            </Tooltip>
          </Box>
          
        </Box>
      </Box>

      
      <Grid container spacing={3} className="mt-4">
        {/* Top Metrics */}
        <Grid item xs={12} sm={6} md={4}>
          {loading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (
            <MetricCard
              title="Emails Sent"
              value={data?.total_sends || 0}
              description="Total emails sent. Click to see unique recipients."
              onValueClick={() => handleOpenContactModal('sent')}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          {loading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (
            <MetricCard
              title={showUnique ? "Unique Opens" : "Total Opens"}
              value={showUnique ? 
                (data?.unique_opens || 0) : 
                (data?.total_opens || 0)
              }
              subtitle={`${formatPercentage(showUnique ? 
                (data?.unique_open_rate || 0) : 
                (data?.open_rate || 0)
              )} rate`}
              description={showUnique ? 
                "Number of unique contacts who opened emails" : 
                "Total number of times emails were opened. This includes multiple opens from the same contact. Click to see the list of unique contacts who opened emails."
              }
              onValueClick={() => handleOpenContactModal('opens')}
              onMapClick={() => handleOpenMapModal('opens')}
              contactCount={data?.unique_opens || 0}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          {loading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (
            <MetricCard
              title={showUnique ? "Unique Clicks" : "Total Clicks"}
              value={showUnique ? 
                data?.unique_clicks || 0 : 
                data?.total_clicks || 0
              }
              subtitle={`${formatPercentage(showUnique ? 
                (data?.unique_click_rate || 0) : 
                (data?.click_rate || 0)
              )} rate, ${formatPercentage(showUnique ? 
                (data?.unique_click_to_open_rate || 0) : 
                (data?.click_to_open_rate || 0)
              )} CTOR`}
              description={showUnique ? 
                "Number of unique contacts who clicked links" : 
                "Total number of link clicks. This includes multiple clicks from the same contact. Click to see the list of unique contacts who clicked links."
              }
              onValueClick={() => handleOpenContactModal('clicks')}
              onMapClick={() => handleOpenMapModal('clicks')}
              contactCount={data?.unique_clicks || 0}
            />
          )}
        </Grid>


        {/* Email Engagement Trend */}
        <Grid item xs={12}>
          <Paper elevation={0} className="p-4 h-full border border-gray-100">
            <Box display="flex" justifyContent="space-between" alignItems="center" className="mb-4">
              <Typography variant="h6" className="text-gray-800 font-medium">
                {chartTitle}
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <Box display="flex" alignItems="center" gap={1}>
                  <Switch
                    checked={showSends}
                    onChange={(e) => setShowSends(e.target.checked)}
                    size="small"
                    color="primary"
                  />
                  <Typography variant="body2" className="text-gray-600 text-sm">
                    Sends
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Switch
                    checked={showOpens}
                    onChange={(e) => setShowOpens(e.target.checked)}
                    size="small"
                    color="primary"
                  />
                  <Typography variant="body2" className="text-gray-600 text-sm">
                    Opens
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Switch
                    checked={showClicks}
                    onChange={(e) => setShowClicks(e.target.checked)}
                    size="small"
                    color="primary"
                  />
                  <Typography variant="body2" className="text-gray-600 text-sm">
                    Clicks
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box className="h-[300px]">
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                chartData && chartData.opens && chartData.opens.length > 0 ? (
                  <ResponsiveLine
                    data={[
                      ...(showSends ? [{
                        id: 'sends',
                        data: chartData.sends
                      }] : []),
                      ...(showOpens ? [{
                        id: 'opens',
                        data: chartData.opens
                      }] : []),
                      ...(showClicks ? [{
                        id: 'clicks',
                        data: chartData.clicks
                      }] : [])
                    ]}
                    margin={{ top: 30, right: 40, bottom: 70, left: 70 }}
                    xScale={{ type: 'point' }}
                    yScale={{
                      type: 'linear',
                      min: 0,
                      max: 'auto',
                      stacked: false,
                      reverse: false
                    }}
                    curve="monotoneX"
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 12,
                      tickRotation: -45,
                      legend: 'Date',
                      legendOffset: 55,
                      legendPosition: 'middle'
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 8,
                      tickRotation: 0,
                      legend: yAxisLabel,
                      legendOffset: -50,
                      legendPosition: 'middle',
                      tickValues: 'auto'
                    }}
                    enableGridX={false}
                    colors={(d) => {
                      const colorMap: {[key: string]: string} = {
                        'sends': 'hsl(142, 70%, 50%)',   // Green
                        'opens': 'hsl(210, 70%, 50%)',   // Blue
                        'clicks': 'hsl(0, 70%, 50%)'     // Red
                      };
                      return colorMap[d.id as string] || 'hsl(0, 0%, 50%)';
                    }}
                    pointSize={8}
                    pointColor={{ theme: 'background' }}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    enableArea={true}
                    areaOpacity={0.15}
                    useMesh={true}
                    animate={false}
                    legends={[
                      {
                        anchor: 'top-right',
                        direction: 'column',
                        justify: false,
                        translateX: 0,
                        translateY: 0,
                        itemsSpacing: 0,
                        itemDirection: 'left-to-right',
                        itemWidth: 80,
                        itemHeight: 20,
                        itemOpacity: 0.75,
                        symbolSize: 12,
                        symbolShape: 'circle',
                        data: [
                          { id: 'sends', label: 'Sends', color: 'hsl(142, 70%, 50%)' },
                          { id: 'opens', label: 'Opens', color: 'hsl(210, 70%, 50%)' },
                          { id: 'clicks', label: 'Clicks', color: 'hsl(0, 70%, 50%)' }
                        ],
                        effects: [
                          {
                            on: 'hover',
                            style: {
                                itemBackground: 'rgba(0, 0, 0, .03)',
                                itemOpacity: 1
                            }
                          }
                        ]
                      }
                    ]}
                    tooltip={({ point }) => {
                      const date = String(point.data.x);
                      
                      // Show sends first, then opens, then clicks
                      const sendsData = chartData.sends;
                      const opensData = chartData.opens;
                      const clicksData = chartData.clicks;
                      const sendsPoint = sendsData?.find(p => p.x === date);
                      const opensPoint = opensData?.find(p => p.x === date);
                      const clicksPoint = clicksData?.find(p => p.x === date);
                      const sendsValue = sendsPoint?.y || 0;
                      const opensValue = opensPoint?.y || 0;
                      const clicksValue = clicksPoint?.y || 0;
                      const formattedSendsValue = sendsValue.toLocaleString();
                      
                      // Calculate percentages for opens and clicks relative to sends
                      const openRate = sendsValue > 0 ? (opensValue / sendsValue) * 100 : 0;
                      const clickRate = sendsValue > 0 ? (clicksValue / sendsValue) * 100 : 0;
                      
                      // Always show count format for the main Email Engagement Over Time graph
                      const formattedOpensValue = `${opensValue.toLocaleString()} (${openRate.toFixed(2)}%)`;
                      const formattedClicksValue = `${clicksValue.toLocaleString()} (${clickRate.toFixed(2)}%)`;
                      
                      return (
                        <div
                          style={{
                            background: 'white',
                            padding: '12px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            minWidth: '150px'
                          }}
                        >
                          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                            {date}
                          </div>
                          <div style={{ marginBottom: '4px', color: '#666' }}>
                            <span style={{ color: '#10b981' }}>●</span> Sends: <strong>{formattedSendsValue}</strong>
                          </div>
                          <div style={{ marginBottom: '4px', color: '#666' }}>
                            <span style={{ color: '#1e40af' }}>●</span> Opens: <strong>{formattedOpensValue}</strong>
                          </div>
                          <div style={{ color: '#666' }}>
                            <span style={{ color: '#dc2626' }}>●</span> Clicks: <strong>{formattedClicksValue}</strong>
                          </div>
                        </div>
                      );
                    }}
                  />
                ) : (
                  <Box className="flex items-center justify-center h-full">
                    <Typography variant="body2" className="text-gray-500">
                      No data available for the selected date range
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          </Paper>
        </Grid>

        {/* View Mode Toggle - Below Email Engagement Over Time */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" className="mb-4">
            <Box className="bg-gray-100 rounded-full p-0.5 inline-flex">
              <button
                onClick={() => handleViewModeChange('count')}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                  viewMode === 'count'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Count View
              </button>
              <button
                onClick={() => handleViewModeChange('rate')}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                  viewMode === 'rate'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Rate View
              </button>
            </Box>
          </Box>
        </Grid>

        {/* Segment Charts - Count View */}
        {viewMode === 'count' && (
          <>
            {/* Opens by Segment */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} className="p-4 h-full border border-gray-100">
                <Box display="flex" alignItems="center" gap={1} className="mb-4">
                  <Typography variant="h6" className="text-gray-800 font-medium">
                    Opens by Segment
                  </Typography>
                  <Tooltip 
                    title={
                      <Box>
                        <Typography variant="body2" color="white" sx={{ mb: 1 }}>
                          <strong>Opens by Segment</strong>
                        </Typography>
                        <Typography variant="body2" color="rgba(255, 255, 255, 0.8)">
                          Shows the total number of email opens per segment in the selected time period.
                        </Typography>
                        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ fontStyle: 'italic', mt: 1 }}>
                          Note: Contacts can belong to multiple segments, so segment totals may exceed unique counts.
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="top"
                    sx={{
                      backgroundColor: 'rgba(0, 0, 0, 0.95)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      '& .MuiTooltip-arrow': {
                        color: 'rgba(0, 0, 0, 0.95)',
                      },
                    }}
                  >
                    <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                <Box className="h-[300px] [&_text]:text-lg [&_text]:font-bold">
                  {loading ? (
                    <Skeleton variant="rectangular" height={300} />
                  ) : opensBySegment.length > 0 ? (
                    <ResponsiveBar
                      data={opensBySegment.map(segment => ({
                        id: segment.segment,
                        label: segment.segment,
                        value: segment.opens,
                        color: getSegmentColor(segment.segment)
                      }))}
                      keys={['value']}
                      indexBy="label"
                      margin={{ top: 20, right: 20, bottom: 100, left: 80 }}
                      padding={0.3}
                      valueScale={{ type: 'linear' }}
                      colors={{ datum: 'data.color' }}
                      borderRadius={2}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: -60,
                        legend: 'Segment',
                        legendPosition: 'middle',
                        legendOffset: 80
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Opens',
                        legendPosition: 'middle',
                        legendOffset: -50
                      }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                      animate={false}
                      tooltip={({ data }) => (
                        <div
                          style={{
                            background: 'white',
                            padding: '12px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                            {data.label}
                          </div>
                          <div style={{ color: '#666' }}>
                            Opens: <strong>{data.value.toLocaleString()}</strong>
                          </div>
                        </div>
                      )}
                    />
                  ) : (
                    <Box className="flex items-center justify-center h-full">
                      <Typography variant="body2" className="text-gray-500">
                        No segment data available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Clicks by Segment */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} className="p-4 h-full border border-gray-100">
                <Box display="flex" alignItems="center" gap={1} className="mb-4">
                  <Typography variant="h6" className="text-gray-800 font-medium">
                    Clicks by Segment
                  </Typography>
                  <Tooltip 
                    title={
                      <Box>
                        <Typography variant="body2" color="white" sx={{ mb: 1 }}>
                          <strong>Clicks by Segment</strong>
                        </Typography>
                        <Typography variant="body2" color="rgba(255, 255, 255, 0.8)">
                          Shows the total number of email clicks per segment in the selected time period.
                        </Typography>
                        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ fontStyle: 'italic', mt: 1 }}>
                          Note: Contacts can belong to multiple segments, so segment totals may exceed unique counts.
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="top"
                    sx={{
                      backgroundColor: 'rgba(0, 0, 0, 0.95)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      '& .MuiTooltip-arrow': {
                        color: 'rgba(0, 0, 0, 0.95)',
                      },
                    }}
                  >
                    <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                <Box className="h-[300px] [&_text]:text-lg [&_text]:font-bold">
                  {loading ? (
                    <Skeleton variant="rectangular" height={300} />
                  ) : opensBySegment.length > 0 ? (
                    <ResponsiveBar
                      data={opensBySegment.map(segment => ({
                        id: segment.segment,
                        label: segment.segment,
                        value: segment.clicks,
                        color: getSegmentColor(segment.segment)
                      }))}
                      keys={['value']}
                      indexBy="label"
                      margin={{ top: 20, right: 20, bottom: 100, left: 60 }}
                      padding={0.3}
                      valueScale={{ type: 'linear' }}
                      colors={{ datum: 'data.color' }}
                      borderRadius={2}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: -60,
                        legend: 'Segment',
                        legendPosition: 'middle',
                        legendOffset: 80
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Clicks',
                        legendPosition: 'middle',
                        legendOffset: -40
                      }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                      animate={false}
                      tooltip={({ data }) => (
                        <div
                          style={{
                            background: 'white',
                            padding: '12px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                            {data.label}
                          </div>
                          <div style={{ color: '#666' }}>
                            Clicks: <strong>{data.value.toLocaleString()}</strong>
                          </div>
                        </div>
                      )}
                    />
                  ) : (
                    <Box className="flex items-center justify-center h-full">
                      <Typography variant="body2" className="text-gray-500">
                        No segment data available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </>
        )}

        {/* Segment Charts - Rate View */}
        {viewMode === 'rate' && (
          <>
            {/* Open Rates by Segment */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} className="p-4 h-full border border-gray-100">
                <Box display="flex" alignItems="center" gap={1} className="mb-4">
                  <Typography variant="h6" className="text-gray-800 font-medium">
                    Open Rates by Segment
                  </Typography>
                  <Tooltip 
                    title={
                      <Box>
                        <Typography variant="body2" color="white" sx={{ mb: 1 }}>
                          <strong>Open Rate by Segment</strong>
                        </Typography>
                        <Typography variant="body2" color="rgba(255, 255, 255, 0.8)">
                          Shows the percentage of emails opened per segment, calculated as (opens in segment / total sends) × 100.
                        </Typography>
                        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ fontStyle: 'italic', mt: 1 }}>
                          Note: Contacts can belong to multiple segments, so segment totals may exceed unique counts.
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="top"
                    sx={{
                      backgroundColor: 'rgba(0, 0, 0, 0.95)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      '& .MuiTooltip-arrow': {
                        color: 'rgba(0, 0, 0, 0.95)',
                      },
                    }}
                  >
                    <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                <Box className="h-[300px] [&_text]:text-lg [&_text]:font-bold">
                  {loading ? (
                    <Skeleton variant="rectangular" height={300} />
                  ) : opensBySegment.length > 0 ? (
                    <ResponsiveBar
                      data={opensBySegment.map(segment => ({
                        id: segment.segment,
                        label: segment.segment,
                        value: segment.open_rate, // Already a percentage
                        color: getSegmentColor(segment.segment)
                      }))}
                      keys={['value']}
                      indexBy="label"
                      margin={{ top: 20, right: 20, bottom: 100, left: 60 }}
                      padding={0.3}
                      valueScale={{ type: 'linear' }}
                      colors={{ datum: 'data.color' }}
                      borderRadius={2}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: -60,
                        legend: 'Segment',
                        legendPosition: 'middle',
                        legendOffset: 80
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Open Rate (%)',
                        legendPosition: 'middle',
                        legendOffset: -50
                      }}
                      enableLabel={false}
                      animate={false}
                      tooltip={({ data }) => (
                        <div
                          style={{
                            background: 'white',
                            padding: '12px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                            {data.label}
                          </div>
                          <div style={{ color: '#666' }}>
                            Open Rate: <strong>{data.value.toFixed(2)}%</strong>
                          </div>
                        </div>
                      )}
                    />
                  ) : (
                    <Box className="flex items-center justify-center h-full">
                      <Typography variant="body2" className="text-gray-500">
                        No segment data available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Click Rate / CTOR by Segment */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} className="p-4 h-full border border-gray-100">
                <Box display="flex" justifyContent="space-between" alignItems="center" className="mb-4">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" className="text-gray-800 font-medium">
                      {clickRateMode === 'ctor' ? 'Click to Open Rate (CTOR) by Segment' : 'Click Rate by Segment'}
                    </Typography>
                    <Tooltip 
                      title={
                        <Box>
                          <Typography variant="body2" color="white" sx={{ mb: 1 }}>
                            <strong>{clickRateMode === 'ctor' ? 'Click to Open Rate (CTOR) by Segment' : 'Click Rate by Segment'}</strong>
                          </Typography>
                          <Typography variant="body2" color="rgba(255, 255, 255, 0.8)">
                            {clickRateMode === 'ctor' 
                              ? 'Shows the percentage of opens that resulted in clicks per segment, calculated as (clicks in segment / opens in segment) × 100.'
                              : 'Shows the percentage of emails clicked per segment, calculated as (clicks in segment / total sends) × 100.'
                            }
                          </Typography>
                          <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ fontStyle: 'italic', mt: 1 }}>
                            Note: Contacts can belong to multiple segments, so segment totals may exceed unique counts.
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                      sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.95)',
                        color: 'white',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(0, 0, 0, 0.95)',
                        },
                      }}
                    >
                      <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  
                  {/* CTOR Toggle */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Switch
                      checked={clickRateMode === 'ctor'}
                      onChange={(e) => setClickRateMode(e.target.checked ? 'ctor' : 'send')}
                      size="small"
                      color="primary"
                    />
                    <Typography variant="body2" className="text-gray-600 text-sm">
                      CTOR
                    </Typography>
                  </Box>
                </Box>
                <Box className="h-[300px] [&_text]:text-lg [&_text]:font-bold">
                  {loading ? (
                    <Skeleton variant="rectangular" height={300} />
                  ) : opensBySegment.length > 0 ? (
                    <ResponsiveBar
                      data={opensBySegment.map(segment => ({
                        id: segment.segment,
                        label: segment.segment,
                        value: clickRateMode === 'send' ? segment.click_rate : (segment as any).click_to_open_rate, // Already a percentage
                        color: getSegmentColor(segment.segment)
                      }))}
                      keys={['value']}
                      indexBy="label"
                      margin={{ top: 20, right: 20, bottom: 100, left: 60 }}
                      padding={0.3}
                      valueScale={{ type: 'linear' }}
                      colors={{ datum: 'data.color' }}
                      borderRadius={2}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: -60,
                        legend: 'Segment',
                        legendPosition: 'middle',
                        legendOffset: 80
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Click Rate (%)',
                        legendPosition: 'middle',
                        legendOffset: -50
                      }}
                      enableLabel={false}
                      animate={false}
                      tooltip={({ data }) => (
                        <div
                          style={{
                            background: 'white',
                            padding: '12px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                            {data.label}
                          </div>
                          <div style={{ color: '#666' }}>
                            Click Rate: <strong>{data.value.toFixed(2)}%</strong>
                          </div>
                        </div>
                      )}
                    />
                  ) : (
                    <Box className="flex items-center justify-center h-full">
                      <Typography variant="body2" className="text-gray-500">
                        No segment data available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </>
        )}

      </Grid>

      {/* Contact List Modal */}
      <ContactListModal
        key={`contact-modal-${effectiveClientId}-${selectedMetricForModal}`}
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        title="Contacts"
        subtitle={`${
          selectedMetricForModal === 'sent' ? 'Unique recipients (contacts who received emails)' :
          selectedMetricForModal === 'opens' ? 'Contacts who opened emails' :
          selectedMetricForModal === 'clicks' ? 'Contacts who clicked emails' :
          'contacts'
        }${selectedMetricForModal !== 'sent' ? (includeSpamChecker ? ' (excluding likely spam checker clicks)' : ' (including likely spam checker clicks)') : ''}`}
        dataSource="engagement"
        initialIncludeSpamChecker={includeSpamChecker}
        config={useMemo(() => {
          if (!selectedMetricForModal) {
            return null;
          }
          return {
            engagementMetric: selectedMetricForModal,
            engagementSortBy: 'top' as const,
            clientId: effectiveClientId!,
            dateRange: {
              date_range: dateRange.date_range,
              startDate: dateRange.startDate,
              endDate: dateRange.endDate
            }
          };
        }, [selectedMetricForModal, effectiveClientId, dateRange])}
      />

      {/* Geographic Map Modal */}
      <GeographicMapModal
        key={`map-modal-${effectiveClientId}-${selectedMetricForMap}`}
        open={mapModalOpen}
        onClose={handleCloseMapModal}
        title="Geographic Distribution"
        subtitle={`${selectedMetricForMap === 'opens' ? 'Email opens by location' : 'Email clicks by location'}${includeSpamChecker ? ' (excluding likely spam checker clicks)' : ' (including likely spam checker clicks)'}`}
        clientId={effectiveClientId!}
        mapData={mapData}
        loading={mapLoading}
        error={mapError}
      />
    </Box>
  );
};

export default AnalyticsOverview;